import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Plus, Search, Building2, Loader2, Printer } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { cn } from "@/lib/cn";
import { useFunder } from "@/funder/FunderContext";
import { Button } from "@/components/ui-kit/Button";
import { Input } from "@/components/ui-kit/Input";
import { Card } from "@/components/ui-kit/Card";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui-kit/Table";
import { Pill } from "@/components/ui-kit/Pill";
import { ClientFormDialog } from "@/components/clients/ClientFormDialog";
import { ClientActions } from "@/components/clients/ClientActions";
import { useIntakes } from "@/intakes/IntakesContext";

const TABS = ["All", "ACSA", "Client", "Active", "Inactive", "Open Matters", "Closed Matters", "Ready to Invoice", "Unpaid/Partial", "On Hold", "Archived"];

function Levels({ levels }) {
  if (!levels) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex gap-1">
      {["l1", "l2", "l3"].map((k) => (
        <span key={k} className={cn("rounded px-1 py-0.5 text-[10px] font-semibold uppercase", levels[k] ? "bg-status-paid/15 text-status-paid" : "bg-secondary text-muted-foreground/40")}>
          {k}
        </span>
      ))}
    </span>
  );
}

function Balance({ b }) {
  if (!b) return <span className="text-muted-foreground">—</span>;
  if (b.balance > 0) return <span className="nums font-medium text-foreground">{money(b.balance)}</span>;
  if (b.credit > 0) return <span className="nums text-status-paid">{money(b.credit)} cr</span>;
  return <span className="nums text-muted-foreground">{money(0)}</span>;
}

export function ClientsBoardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { abbr, typeLabel } = useFunder();
  const { refresh: refreshIntakes } = useIntakes();
  const tabLabel = (t) => (t === "ACSA" ? abbr : t);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("All");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);

  const load = () =>
    api.get("/board?include_archived=true&include_hold=true").then((r) => setRows(r.data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  useEffect(() => {
    load();
  }, []);

  // Pre-fill the New Client dialog from a client intake (Settings → "Start new client
  // from this"). Only pre-populates the existing form — never auto-creates anything.
  useEffect(() => {
    const pc = location.state?.prefillClient;
    if (pc) {
      setPrefill(pc);
      setDialogOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const onClientSaved = async () => {
    // If this client came from an intake, mark that intake handled (best-effort).
    if (prefill?.intakeId) {
      try {
        await api.patch(`/intakes/${prefill.intakeId}`, { status: "handled" });
        refreshIntakes();
      } catch {
        /* non-fatal — the client was still created */
      }
    }
    setPrefill(null);
    load();
  };

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !r.client.name.toLowerCase().includes(q)) return false;
      if (tab === "Archived") return r.client.archived;
      if (tab === "On Hold") return r.client.on_hold && !r.client.archived;
      if (r.client.archived || r.client.on_hold) return false; // archived/on-hold live only under their own filters
      const m = r.matter;
      switch (tab) {
        case "ACSA": return m?.type === "ACSA_Client";
        case "Client": return m?.type === "Client";
        case "Active": return r.has_active;
        case "Inactive": return !r.has_active;
        case "Open Matters": return r.has_active;
        case "Closed Matters": return r.closed_count > 0;
        case "Ready to Invoice": return r.ready_to_invoice;
        case "Unpaid/Partial": return r.unpaid_partial;
        default: return true;
      }
    });
  }, [rows, query, tab]);

  return (
    <div className="space-y-6" data-testid="clients-board-page">
      <div className="flex flex-wrap items-end justify-between gap-4 print:hidden">
        <div>
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground">Clients</h1>
          <p className="mt-2 text-sm text-muted-foreground">{"Each client's single active matter, at a glance."}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => window.print()} data-testid="print-button">
            <Printer className="h-4 w-4" /> Print
          </Button>
          <Button onClick={() => setDialogOpen(true)} data-testid="new-client-button">
            <Plus className="h-4 w-4" /> New client
          </Button>
        </div>
      </div>

      <div className="relative max-w-sm print:hidden">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="board-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search clients…" className="pl-9" />
      </div>

      <div className="flex flex-wrap gap-1.5 print:hidden" data-testid="board-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            data-testid={`board-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => setTab(t)}
            className={cn(
              "rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
              tab === t ? "border-primary/40 bg-primary/10 text-primary" : "border-border bg-surface text-muted-foreground hover:bg-accent"
            )}
          >
            {tabLabel(t)}
          </button>
        ))}
      </div>

      <Card>
        {error ? (
          <div className="p-6 text-sm text-destructive">{error}</div>
        ) : rows === null ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <span className="grid h-12 w-12 place-items-center rounded-lg bg-secondary text-muted-foreground"><Building2 className="h-6 w-6" /></span>
            <p className="text-sm text-muted-foreground">{query || tab !== "All" ? "No clients match." : "No clients yet — create your first one."}</p>
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Client</TH>
                <TH>Active matter</TH>
                <TH>Type</TH>
                <TH>{abbr}</TH>
                <TH className="text-right">Hours</TH>
                <TH className="text-right">Client bal.</TH>
                <TH className="text-right">{abbr} bal.</TH>
                <TH>Status</TH>
                <TH>Last invoice</TH>
                <TH>Alerts</TH>
                <TH className="text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {filtered.map((r) => (
                <TR key={r.client.id} data-testid={`board-row-${r.client.id}`}>
                  <TD>
                    <button
                      type="button"
                      data-testid={`board-client-name-${r.client.id}`}
                      onClick={() => navigate(`/clients-matters/${r.client.id}`)}
                      className="font-medium text-foreground hover:text-primary hover:underline"
                    >
                      {r.client.name}
                    </button>
                  </TD>
                  <TD>
                    {r.matter ? (
                      <button
                        type="button"
                        data-testid={`board-active-matter-${r.matter.id}`}
                        onClick={() => navigate(`/workbench/${r.matter.id}`)}
                        className="text-primary hover:underline"
                      >
                        {r.matter.name}
                      </button>
                    ) : (
                      <span className="text-muted-foreground">No active matter</span>
                    )}
                  </TD>
                  <TD className="text-muted-foreground">{r.matter ? typeLabel(r.matter.type) : "—"}</TD>
                  <TD>{r.matter?.type === "ACSA_Client" ? <Levels levels={r.matter.levels} /> : <span className="text-muted-foreground">—</span>}</TD>
                  <TD className="nums text-right text-foreground">{r.has_active ? r.hours_approved : "—"}</TD>
                  <TD className="text-right"><Balance b={r.balances?.client} /></TD>
                  <TD className="text-right"><Balance b={r.balances?.acsa} /></TD>
                  <TD>{r.client.archived ? <Pill tone="muted">Archived</Pill> : r.client.on_hold ? <Pill tone="accent">On Hold</Pill> : r.has_active ? <Pill tone="success">Open</Pill> : <Pill tone="muted">Inactive</Pill>}</TD>
                  <TD className="text-muted-foreground">{r.last_invoice ? `${r.last_invoice.number} · ${shortDate(r.last_invoice.date)}` : "—"}</TD>
                  <TD>
                    <div className="flex flex-wrap gap-1">
                      {r.needs_review_count > 0 && <Pill tone="info">{r.needs_review_count} review</Pill>}
                      {r.ready_to_invoice && <Pill tone="accent">Ready</Pill>}
                      {r.unpaid_partial && <Pill tone="muted">Unpaid</Pill>}
                      {r.written_off && <Pill tone="muted" data-testid="client-written-off-pill">Written off</Pill>}
                    </div>
                  </TD>
                  <TD>
                    <ClientActions
                      client={r.client}
                      canHardDelete={r.can_hard_delete}
                      outstanding={(r.balances?.client?.balance || 0) + (r.balances?.acsa?.balance || 0)}
                      openMatterCount={r.has_active ? 1 : 0}
                      variant="row"
                      onChanged={load}
                    />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <ClientFormDialog
        open={dialogOpen}
        onOpenChange={(o) => { setDialogOpen(o); if (!o) setPrefill(null); }}
        prefill={prefill}
        onSaved={onClientSaved}
      />
    </div>
  );
}
